import type { ReactNode } from 'react';
import { cx } from '../cx';
import { NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';
import { LABEL_TEXT, VALUE_TEXT } from './scale';

export type FlowNode = {
  id: string;
  label: ReactNode;
  count: number;
  /** The count formatted for the page ("1 204" in fi); defaults to the plain number. */
  display?: ReactNode;
  /** The stage column, left to right from 0. Nodes in one column split a stage (viewed or saved). */
  column: number;
  /** `story`: the stage the chart is about (sun, ink ring). `context`: a stage that ends a path (rejected). */
  tone?: 'story' | 'context';
  /** A short line under the count ("12% of browsed"), worked out by the page. */
  note?: ReactNode;
};

export type FlowLink = { from: string; to: string; count: number };

export type JobFlowProps = {
  nodes: FlowNode[];
  links: FlowLink[];
  /**
   * Says the bars are scaled ("Bars use a square-root scale so small stages stay visible; the
   * numbers are exact"). Required: browsing dwarfs everything after it, so a linear scale would
   * leave the stages that matter as hairlines.
   */
  scaleNote: ReactNode;
  /** Example data (signed out): the chart is drawn faint with this tag in the corner. */
  example?: ReactNode;
  /** `auto` (default) switches to the narrow form in a narrow container: one row per stage, bars across. */
  layout?: ChartLayout;
  /** One sentence with every stage's count, for screen readers. */
  description: string;
  className?: string;
};

type Placed = FlowNode & { top: number; height: number; x: number };

const NODE_W = 1.6;
const GAP = 6;

/** Places nodes: square-root heights that fit the tallest column, each column centred. */
export function layoutJobFlow(nodes: FlowNode[]): { placed: Placed[]; columns: number } {
  const columns = Math.max(1, ...nodes.map((n) => n.column + 1));
  const byColumn = Array.from({ length: columns }, (_, c) => nodes.filter((n) => n.column === c));
  const root = (n: FlowNode) => Math.sqrt(Math.max(0, n.count));
  // One scale for every column: the column that needs the most room sets it.
  const k = Math.min(
    ...byColumn
      .filter((col) => col.length)
      .map(
        (col) =>
          (100 - GAP * (col.length - 1)) /
          Math.max(
            1e-9,
            col.reduce((s, n) => s + root(n), 0),
          ),
      ),
  );
  const placed: Placed[] = [];
  byColumn.forEach((col, c) => {
    const heights = col.map((n) => Math.max(2, root(n) * k));
    const total = heights.reduce((s, h) => s + h, 0) + GAP * (col.length - 1);
    let top = (100 - total) / 2;
    col.forEach((n, i) => {
      placed.push({ ...n, top, height: heights[i], x: columns === 1 ? 0 : (c / (columns - 1)) * (100 - NODE_W) });
      top += heights[i] + GAP;
    });
  });
  return { placed, columns };
}

/**
 * Where each stage's words sit (percent from the top): centred on the bar, pushed apart within a
 * column so small stages next to each other never overlap, and kept inside the chart.
 */
export function labelPositions(
  placed: Array<{ id: string; column: number; top: number; height: number; note?: unknown }>,
) {
  const out = new Map<string, number>();
  const columns = new Set(placed.map((p) => p.column));
  for (const c of columns) {
    const col = placed.filter((p) => p.column === c).sort((a, b) => a.top - b.top);
    // Two lines take about a fifth of the chart's height; a note adds a third line.
    const size = col.map((p) => (p.note ? 28 : 21));
    const y = col.map((p) => p.top + p.height / 2);
    for (let i = 1; i < col.length; i++) {
      const min = y[i - 1] + (size[i - 1] + size[i]) / 2;
      if (y[i] < min) y[i] = min;
    }
    const overflow = y[col.length - 1] + size[col.length - 1] / 2 - 100;
    if (overflow > 0) for (let i = 0; i < col.length; i++) y[i] -= overflow;
    const underflow = -(y[0] - size[0] / 2);
    if (underflow > 0) for (let i = 0; i < col.length; i++) y[i] += underflow;
    col.forEach((p, i) => out.set(p.id, y[i]));
  }
  return out;
}

/** Each link's band: where it leaves its source and enters its target, stacked in order. */
function bands(placed: Placed[], links: FlowLink[]) {
  const byId = new Map(placed.map((p) => [p.id, p]));
  const outUsed = new Map<string, number>();
  const inUsed = new Map<string, number>();
  const ordered = [...links]
    .filter((l) => l.count > 0 && byId.has(l.from) && byId.has(l.to))
    .sort((a, b) => byId.get(a.to)!.top - byId.get(b.to)!.top || byId.get(a.from)!.top - byId.get(b.from)!.top);
  return ordered.map((l) => {
    const a = byId.get(l.from)!;
    const b = byId.get(l.to)!;
    const ta = (a.height * l.count) / Math.max(1, a.count);
    const tb = (b.height * l.count) / Math.max(1, b.count);
    const ya = a.top + (outUsed.get(a.id) ?? 0);
    const yb = b.top + (inUsed.get(b.id) ?? 0);
    outUsed.set(a.id, (outUsed.get(a.id) ?? 0) + ta);
    inUsed.set(b.id, (inUsed.get(b.id) ?? 0) + tb);
    const x0 = a.x + NODE_W;
    const x1 = b.x;
    const xm = (x0 + x1) / 2;
    return {
      key: `${l.from}-${l.to}`,
      story: b.tone === 'story',
      d: `M${x0},${ya} C${xm},${ya} ${xm},${yb} ${x1},${yb} L${x1},${yb + tb} C${xm},${yb + tb} ${xm},${ya + ta} ${x0},${ya + ta} Z`,
    };
  });
}

const NODE_FILL = { story: 'bg-jc-chart-2 ring-2 ring-jc-ink', context: 'bg-jc-chart-context', default: 'bg-jc-ink' };

/**
 * The reader's own path through their job search: browsed, viewed or saved, applied, and what
 * happened next, as stages joined by flows. Every count is written beside its stage, so nothing
 * hides in a tooltip. Bars use a square-root scale (and say so) because browsing outnumbers
 * applying by a hundred to one. The narrow form is one row per stage. For a market funnel with no
 * branches, `Funnel` is simpler.
 */
export function JobFlow({ nodes, links, scaleNote, example, layout = 'auto', description, className }: JobFlowProps) {
  const { placed } = layoutJobFlow(nodes);
  const paths = bands(placed, links);
  const labelY = labelPositions(placed);
  const max = Math.max(1, ...nodes.map((n) => n.count));
  const ordered = [...placed].sort((a, b) => a.column - b.column || a.top - b.top);
  // Example data fades the shapes only; the words stay at full contrast.
  const faint = example ? 'opacity-40' : undefined;
  return (
    <div className={cx('@container flex flex-col gap-2', className)}>
      {example ? (
        <span className="self-end rounded-jc-info px-1.5 py-0.5 type-eyebrow text-jc-ink-secondary ring-1 ring-jc-border ring-inset">
          {example}
        </span>
      ) : null}
      <div role="img" aria-label={description} className="flex flex-col gap-2">
        {/* Wide: stages left to right, flows between them. */}
        <div className={cx('relative mr-28 h-56', WIDE_ONLY[layout])} aria-hidden="true">
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className={cx('absolute inset-0 size-full overflow-visible', faint)}
          >
            {paths.map((p) => (
              <path
                key={p.key}
                d={p.d}
                className={cx(p.story ? 'fill-jc-chart-2 opacity-60' : 'fill-jc-chart-context opacity-40')}
              />
            ))}
          </svg>
          {placed.map((n) => (
            <span
              key={n.id}
              className={cx('absolute rounded-jc-info', NODE_FILL[n.tone ?? 'default'], faint)}
              style={{ left: `${n.x}%`, width: `${NODE_W}%`, top: `${n.top}%`, height: `${n.height}%` }}
            />
          ))}
          {/* A thin leader where a label had to move off its bar. */}
          {placed.map((n) => {
            const centre = n.top + n.height / 2;
            const y = labelY.get(n.id) ?? centre;
            return Math.abs(y - centre) > 3 ? (
              <span
                key={`${n.id}-leader`}
                className="absolute w-1.5 rounded-bl-jc-info border-b border-l border-jc-ink-secondary"
                style={{
                  left: `${n.x + NODE_W}%`,
                  top: `${Math.min(centre, y)}%`,
                  height: `${Math.abs(y - centre)}%`,
                }}
              />
            ) : null;
          })}
          {placed.map((n) => (
            <span
              key={`${n.id}-label`}
              className="absolute flex -translate-y-1/2 flex-col pl-2 leading-tight whitespace-nowrap"
              style={{ left: `${n.x + NODE_W}%`, top: `${labelY.get(n.id)}%` }}
            >
              <span className="type-figure-sm text-jc-ink">{n.display ?? n.count}</span>
              <span className={LABEL_TEXT}>{n.label}</span>
              {n.note ? <span className="text-xs text-jc-ink-secondary">{n.note}</span> : null}
            </span>
          ))}
        </div>
        {/* Narrow: one row per stage, in order, on the same square-root scale. */}
        <div className={cx('flex flex-col gap-2', NARROW_ONLY[layout])} aria-hidden="true">
          {ordered.map((n) => (
            <div key={n.id} className="flex flex-col gap-0.5">
              <span className="flex items-baseline justify-between gap-2">
                <span className={cx('truncate', LABEL_TEXT)}>{n.label}</span>
                <span className={VALUE_TEXT}>{n.display ?? n.count}</span>
              </span>
              <span className="block h-2.5 w-full rounded-jc-info bg-jc-elevated">
                <span
                  className={cx('block h-full rounded-jc-info', NODE_FILL[n.tone ?? 'default'], faint)}
                  style={{ width: `${Math.max(n.count > 0 ? 2 : 0, Math.sqrt(n.count / max) * 100)}%` }}
                />
              </span>
              {n.note ? <span className="text-xs text-jc-ink-secondary">{n.note}</span> : null}
            </div>
          ))}
        </div>
      </div>
      <p className="type-caption text-jc-ink-secondary">{scaleNote}</p>
    </div>
  );
}
